import { TASK_MAX_INDENT, type ObjectData } from '@os/doc';

/** Actions of the context menu; command-like ones are run by the controller (`runMenuAction`). */
export type MenuAction =
  | 'cut'
  | 'copy'
  | 'paste'
  | 'duplicate'
  | 'delete'
  | 'copyStyle'
  | 'pasteStyle'
  | 'bringToFront'
  | 'bringForward'
  | 'sendBackward'
  | 'sendToBack'
  | 'lock'
  | 'unlock'
  | 'group'
  | 'ungroup'
  | 'alignLeft'
  | 'alignHCenter'
  | 'alignRight'
  | 'alignTop'
  | 'alignVCenter'
  | 'alignBottom'
  | 'distributeH'
  | 'distributeV'
  | 'arrange'
  | 'fitToContent'
  | 'removeSection'
  | 'crop'
  | 'resetCrop'
  | 'editText'
  | 'tableAddRow'
  | 'tableAddCol'
  | 'tableDeleteRow'
  | 'tableDeleteCol'
  | 'taskAdd'
  | 'taskCompact'
  | 'taskToggle'
  | 'taskMoveUp'
  | 'taskMoveDown'
  | 'taskIndent'
  | 'taskOutdent'
  | 'taskDelete'
  | 'openLink'
  | 'collapse'
  | 'expand'
  | 'zoomToSelection'
  | 'selectAll'
  | 'zoomFit'
  | 'exportSelection'
  | 'exportCsv'
  | 'importFile'
  | 'moveToPage';
export type MenuItem =
  { id: MenuAction; danger?: boolean; disabled?: boolean } | { separator: true };
export type MenuContext = {
  readOnly: boolean;
  canPaste: boolean;
  canPasteStyle?: boolean;
  cell?: { rowId: string; colId: string } | null;
  /** The task under the pointer when the menu opened on a task list (P16-5). */
  task?: string | null;
  canExport?: boolean;
  canImportFile?: boolean;
  /** The board has another page to move the selection to (P14). */
  canMoveToPage?: boolean;
  /** The effective lock (itself or a locked section around it, P5d); the object's own flag when absent. */
  isLocked?: (o: Readonly<ObjectData>) => boolean;
};

const STYLED = new Set(['sticky', 'shape', 'text', 'section', 'connector']);

const sep: MenuItem = { separator: true };

/** The context menu for a selection (pure: the UI renders and labels it). Separators never lead, trail or repeat. */
export function menuItemsFor(
  sel: ReadonlyArray<Readonly<ObjectData>>,
  ctx: MenuContext,
): MenuItem[] {
  const out: MenuItem[] = [];
  const add = (...items: MenuItem[]) => out.push(...items);
  const one = sel.length === 1 ? sel[0]! : null;
  const isLink = one?.type === 'link' || one?.type === 'embed';
  if (sel.length === 0) {
    if (!ctx.readOnly) add({ id: 'paste', disabled: !ctx.canPaste }, sep, { id: 'selectAll' });
    if (!ctx.readOnly && ctx.canImportFile) add({ id: 'importFile' });
    add({ id: 'zoomFit' });
    return tidy(out);
  }
  if (ctx.readOnly) {
    add({ id: 'copy' }, { id: 'zoomToSelection' });
    if (isLink) add({ id: 'openLink' });
    if (ctx.canExport) add(sep, { id: 'exportSelection' });
    if (ctx.canExport && one?.type === 'table') add({ id: 'exportCsv' });
    return tidy(out);
  }
  const lockedOf = (o: Readonly<ObjectData>) => ctx.isLocked?.(o) ?? o.locked;
  const locked = sel.every(lockedOf);
  // Locked only by a section around it: the toggle would not unlock it (the section's padlock does).
  const inherited = sel.some((o) => lockedOf(o) && !o.locked);
  add(
    { id: 'cut', disabled: locked },
    { id: 'copy' },
    { id: 'paste', disabled: !ctx.canPaste },
    { id: 'duplicate' },
    sep,
  );
  if (sel.every((o) => STYLED.has(o.type))) {
    if (one) add({ id: 'copyStyle' });
    add({ id: 'pasteStyle', disabled: !ctx.canPasteStyle || locked }, sep);
  }
  if (one?.type === 'image')
    add(
      { id: 'crop', disabled: lockedOf(one) || one.rotation % 360 !== 0 },
      {
        id: 'resetCrop',
        disabled: one.crop.w === 1 && one.crop.h === 1 && one.crop.x === 0 && one.crop.y === 0,
      },
      sep,
    );
  if (one && (one.type === 'text' || one.type === 'sticky' || one.type === 'shape'))
    add({ id: 'editText', disabled: lockedOf(one) }, sep);
  if (one?.type === 'table') {
    add({ id: 'tableAddRow' }, { id: 'tableAddCol' });
    if (ctx.cell)
      add(
        { id: 'tableDeleteRow', disabled: one.rows.length <= 1 },
        { id: 'tableDeleteCol', disabled: one.cols.length <= 1 },
      );
    add(sep);
  }
  if (one?.type === 'tasklist' && !lockedOf(one)) {
    add({ id: 'taskAdd' }, { id: 'taskCompact' });
    const i = one.items.findIndex((t) => t.id === ctx.task);
    const t = one.items[i];
    if (t)
      add(
        sep,
        { id: 'taskToggle' },
        { id: 'taskMoveUp', disabled: i === 0 },
        { id: 'taskMoveDown', disabled: i === one.items.length - 1 },
        { id: 'taskIndent', disabled: t.indent >= TASK_MAX_INDENT },
        { id: 'taskOutdent', disabled: t.indent <= 0 },
        { id: 'taskDelete', danger: true },
      );
    add(sep);
  }
  if (isLink) add({ id: 'openLink' }, sep);
  if (one?.type === 'section')
    add(
      { id: one.collapsed ? 'expand' : 'collapse' },
      { id: 'zoomToSelection' },
      ...(lockedOf(one) ? [] : [{ id: 'arrange' } as const]),
      ...(lockedOf(one) || ctx.readOnly ? [] : [{ id: 'fitToContent' } as const, { id: 'removeSection' } as const]),
      sep,
    );
  add(
    { id: 'bringToFront' },
    { id: 'bringForward' },
    { id: 'sendBackward' },
    { id: 'sendToBack' },
    sep,
  );
  add({ id: locked ? 'unlock' : 'lock', disabled: inherited });
  if (sel.length >= 2) add({ id: 'group' });
  if (sel.some((o) => o.type === 'group')) add({ id: 'ungroup' });
  if (sel.length >= 2)
    add(
      sep,
      { id: 'alignLeft' },
      { id: 'alignHCenter' },
      { id: 'alignRight' },
      { id: 'alignTop' },
      { id: 'alignVCenter' },
      { id: 'alignBottom' },
    );
  if (sel.length >= 3) add({ id: 'distributeH' }, { id: 'distributeV' });
  if (sel.length >= 2) add({ id: 'arrange' });
  if (ctx.canMoveToPage) add(sep, { id: 'moveToPage', disabled: locked });
  if (ctx.canExport) add(sep, { id: 'exportSelection' });
  if (ctx.canExport && one?.type === 'table') add({ id: 'exportCsv' });
  add(sep, { id: 'delete', danger: true, disabled: locked });
  return tidy(out);
}

function tidy(items: MenuItem[]): MenuItem[] {
  const out: MenuItem[] = [];
  for (const it of items) {
    if ('separator' in it && (out.length === 0 || 'separator' in out.at(-1)!)) continue;
    out.push(it);
  }
  while (out.length && 'separator' in out.at(-1)!) out.pop();
  return out;
}
